import type { FC, ReactNode } from 'react'
import { useTObject } from '@w/fe-i18n-platform'
import { BaseDomainError, isDev, unknownErrorToError } from '@w/fe-utils'
import { Bug, RefreshCw } from 'lucide-react'
import { cn } from '../lib/utils'
import {
  type FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
  type ErrorBoundaryProps as ReactErrorBoundaryProps,
} from 'react-error-boundary'

interface ErrorBoundaryProps {
  fallback?: FC<{ error: unknown | null; resetErrorBoundary?: () => void }> | ReactNode
  children: ReactErrorBoundaryProps['children']
  resetKeys?: ReactErrorBoundaryProps['resetKeys']
  onReset?: ReactErrorBoundaryProps['onReset']
}

export function ErrorBoundary({ children, resetKeys, onReset, ...props }: ErrorBoundaryProps) {
  const FallbackComponent:
    | FC<{ error: unknown | null; resetErrorBoundary?: () => void }>
    | ReactNode = props.fallback || ErrorFallback
  return (
    <ErrorBoundaryConfig.ErrorBoundary
      children={children}
      fallbackRender={
        typeof FallbackComponent === 'function'
          ? ({ error, resetErrorBoundary }: FallbackProps) => (
              <FallbackComponent error={error} resetErrorBoundary={resetErrorBoundary} />
            )
          : () => <>{FallbackComponent}</>
      }
      resetKeys={resetKeys}
      onReset={onReset}
    />
  )
}

interface ErrorFallbackProps {
  error?: unknown | null
  title?: string
  description?: string
  isMinimal?: boolean
}

function useErrorBoundaryDomain(unknownError: unknown | null): {
  error?: Error
  domainText?: string
} {
  const { t } = useTObject()

  const mappedError =
    unknownError && ErrorBoundaryConfig.errorMapper
      ? ErrorBoundaryConfig.errorMapper(unknownError)
      : unknownError
  const error = mappedError ? unknownErrorToError(mappedError) : undefined

  const getTrpcDomainUserMessage = (
    value: unknown,
  ): { key: string; params?: Record<string, unknown> } | undefined => {
    if (!value || typeof value !== 'object') return undefined
    const data = (value as { data?: unknown }).data
    if (!data || typeof data !== 'object') return undefined
    const de = (data as { domainError?: unknown }).domainError
    if (!de || typeof de !== 'object') return undefined
    const userMessage = (de as { userMessage?: unknown }).userMessage
    if (!Array.isArray(userMessage)) return undefined
    const [key, params] = userMessage
    if (typeof key !== 'string') return undefined
    const paramsObj =
      params && typeof params === 'object' ? (params as Record<string, unknown>) : undefined
    return { key, params: paramsObj }
  }

  const getDomainUserMessage = (
    value: unknown,
  ): { key: string; params?: Record<string, unknown> } | undefined => {
    if (value instanceof BaseDomainError) {
      const parts = value.userMessage || []
      const key = typeof parts[0] === 'string' ? (parts[0] as string) : undefined
      if (!key) return undefined
      const params =
        parts[1] && typeof parts[1] === 'object' ? (parts[1] as Record<string, unknown>) : undefined
      return { key, params }
    }
    return getTrpcDomainUserMessage(value)
  }

  const domainText = (() => {
    const msg = getDomainUserMessage(mappedError)
    if (!msg) return undefined
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    return t(msg.key as any, msg.params)
  })()

  return { error, domainText }
}

export function ErrorFallback({ title, description, isMinimal, ...props }: ErrorFallbackProps) {
  const { t } = useTObject()
  const { error, domainText } = useErrorBoundaryDomain(props.error ?? null)

  const message = isDev && !!error?.message && (
    <p className="break-words text-sm text-destructive">Error: {error.message}</p>
  )

  if (isMinimal) {
    return (
      <div className="flex flex-col items-center justify-center text-center px-4">
        <p className="text-sm font-medium mb-1">
          {title ?? domainText ?? t('platform:error.generic.message')}
        </p>
        {message}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4 gap-4">
      <ErrorBoundaryConfig.ErrorFallbackIllustration />
      <div>
        {!domainText && (
          <h1 className="text-sm font-medium mb-1">{title ?? t('platform:error.generic.title')}</h1>
        )}
        <p className="text-sm text-muted-foreground max-w-md">
          {description ?? domainText ?? t('platform:error.generic.description')}
        </p>
      </div>
      <ErrorBoundaryConfig.ErrorFallbackAction />
      {message}
    </div>
  )
}

export function ErrorFallbackMinimal(props: Omit<ErrorFallbackProps, 'isMinimal'>) {
  return <ErrorFallback {...props} isMinimal />
}

interface RetriableErrorFallbackProps {
  error: unknown | null
  title: string
  retry: React.ReactNode
}
export function RetriableErrorFallback({
  title,
  retry,
  error: unknownError,
}: RetriableErrorFallbackProps) {
  const { error, domainText } = useErrorBoundaryDomain(unknownError)

  const message = isDev && !!error?.message && (
    <p className="break-words text-sm text-destructive">Error: {error.message}</p>
  )

  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4 gap-4">
      {/* <img src={publicUrl('/server-search.svg')} alt="Error illustration" className="w-[140px]" /> */}
      <ErrorBoundaryConfig.RetriableErrorFallbackIllustration />
      <p className="text-sm text-muted-foreground">{domainText ?? title}</p>
      {message}
      {retry}
    </div>
  )
}

function RetriableErrorFallbackIllustration({ className }: { className?: string }) {
  return (
    <div
      className={cn('relative flex items-center justify-center w-32 h-32', className)}
      role="img"
      aria-label="Retriable error illustration"
    >
      {/* Background fragments */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-24 h-24">
          {/* Top left triangle */}
          <div
            className="absolute top-0 left-0 w-12 h-12 bg-muted/70"
            style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
          />

          {/* Bottom right triangle */}
          <div
            className="absolute bottom-0 right-0 w-12 h-12 bg-muted/70"
            style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
          />

          {/* Center square */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-muted rotate-45" />
        </div>
      </div>

      {/* Thin border */}
      <div className="absolute w-20 h-20 border border-border rounded-sm" />

      {/* Main icon */}
      <div className="relative z-10">
        <RefreshCw size={36} className="text-foreground" strokeWidth={1.5} />
      </div>
    </div>
  )
}

function ErrorFallbackIllustration({ className }: { className?: string }) {
  return (
    <div
      className={cn('relative flex items-center justify-center w-32 h-32', className)}
      role="img"
      aria-label="Error illustration"
    >
      {/* Background element */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 rounded-full border-2 border-dashed border-border opacity-50" />
      </div>

      {/* Decorative elements */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-24 h-24 flex items-center justify-center">
          {/* Code brackets */}
          <div className="absolute left-2 h-12 w-3 border-t-2 border-l-2 border-b-2 border-border" />
          <div className="absolute right-2 h-12 w-3 border-t-2 border-r-2 border-b-2 border-border" />
        </div>
      </div>

      {/* Main icon */}
      <div className="relative z-10">
        <Bug size={36} className="text-foreground" strokeWidth={1.5} />
      </div>
    </div>
  )
}

export const ErrorBoundaryConfig = {
  ErrorBoundary: DefaultErrorBoundary as ErrorBoundaryComponent,
  RetriableErrorFallbackIllustration,
  ErrorFallbackIllustration,
  ErrorFallbackAction: (() => null) as FC<{ className?: string }>,
  onError: (() => {}) as (error: Error | unknown, info: { componentStack?: string }) => void,
  errorMapper: undefined as ((error: unknown) => unknown) | undefined,
}

// for both react-error-boundary and sentry error boundary
type ErrorBoundaryComponent = React.ComponentType<a> & {
  new (
    props: a,
  ): React.Component<a, a> & {
    componentDidCatch(error: Error | unknown, errorInfo: React.ErrorInfo): void
    resetErrorBoundary?(...args: a[]): void
    render(): ReactNode
  }
}

type DefaultErrorBoundaryProps = {
  children: ReactErrorBoundaryProps['children']
  fallbackRender: NonNullable<ReactErrorBoundaryProps['fallbackRender']>
  resetKeys?: ReactErrorBoundaryProps['resetKeys']
  onReset?: ReactErrorBoundaryProps['onReset']
}

function DefaultErrorBoundary(props: DefaultErrorBoundaryProps) {
  const { children, fallbackRender, resetKeys, onReset } = props

  return (
    <ReactErrorBoundary
      fallbackRender={fallbackRender}
      onError={(error, info) =>
        ErrorBoundaryConfig.onError?.(error, {
          componentStack: info.componentStack ?? undefined,
        })
      }
      resetKeys={resetKeys}
      onReset={onReset}
    >
      {children}
    </ReactErrorBoundary>
  )
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type a = any
